/**
 * Confirm modal for board drags that move an account's lifecycle position —
 * between phases (client kanban) or between configured statuses (internal
 * status board).
 *
 * Forward moves never complete passed-over steps silently: the modal fetches
 * the account, computes the sub-stages the drop passes over (the same
 * `forwardJumpSubStages` the server validates with), and requires a resolution
 * for each — the date it actually happened, or a skip with a required reason —
 * plus the move's own effective date. Backward moves keep the simple
 * "skipping phases" confirmation.
 *
 * Adjacent backward moves bypass this modal and fire the mutation immediately.
 * Disallowed moves (per `_canTransition`) never reach this modal — they're
 * blocked at the drop-target level in AccountKanban.
 */

import React from 'react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import type {AccountsSubStage, LifecyclePhase} from '@api/generated/client';
import {trpc} from '@common-web/lib/trpc';
import {
  PHASE_LABEL,
  PHASE_ORDER,
  SUB_STAGE_LABEL,
} from '@common-web/components/account/phaseLabels';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  findPositionBySystemSubStage,
  findSubStageByKey,
  firstSubStageOfPhase,
  forwardJumpSubStages,
  isForwardSubStageMove,
} from '@common/accountsView';
import {
  StageResolutionFields,
  buildStageResolutions,
  dateInputToDate,
  todayDateInputValue,
  type StageResolution,
  type StageResolutionDraft,
} from './StageResolutionFields';

/** A board drop awaiting confirmation. */
export type PendingBoardMove =
  | {kind: 'phase'; accountsId: string; fromPhase: LifecyclePhase; toPhase: LifecyclePhase}
  | {kind: 'viewSubStage'; accountsId: string; viewSubStageKey: string};

export interface BoardMoveDetails {
  stageResolutions: StageResolution[];
  effectiveDate: Date | null;
}

interface SkipConfirmModalProps {
  open: boolean;
  move: PendingBoardMove | null;
  onConfirm: (details: BoardMoveDetails) => void;
  onCancel: () => void;
  confirming?: boolean;
}

export function SkipConfirmModal({
  open,
  move,
  onConfirm,
  onCancel,
  confirming = false,
}: SkipConfirmModalProps): React.ReactElement | null {
  const config = useAccountsViewConfig();
  const [resolutionDrafts, setResolutionDrafts] = React.useState<
    Record<string, StageResolutionDraft>
  >({});
  const [effectiveDate, setEffectiveDate] = React.useState(todayDateInputValue);

  React.useEffect(() => {
    if (open) {
      setResolutionDrafts({});
      setEffectiveDate(todayDateInputValue());
    }
  }, [open, move?.accountsId]);

  // Authoritative current position + tenant Welcome Call flag — card payloads
  // don't carry the flag, and the server computes the required resolutions
  // from exactly these values.
  const detail = trpc.account.getById.useQuery(
    {accountsId: move?.accountsId ?? ''},
    {enabled: open && !!move},
  );

  if (!move) return null;

  const currentSubStage = detail.data?.subStage ?? null;
  const welcomeCallEnabled = detail.data?.welcomeCallEnabled ?? true;

  // The canonical sub-stage this drop lands on — mirrors the server:
  // transitionPhase lands on the phase's entry sub-stage; setViewSubStage on
  // the chosen status' canonical mapping.
  const landingSubStage: AccountsSubStage | null =
    move.kind === 'viewSubStage'
      ? ((findSubStageByKey(config, move.viewSubStageKey)?.subStage.systemSubStage ??
          null) as AccountsSubStage | null)
      : move.toPhase === 'LEAD'
        ? 'NEW_LEAD'
        : move.toPhase === 'IMPLEMENTATION'
          ? 'RFC_RECEIVED'
          : move.toPhase === 'ACTIVE'
            ? 'ACTIVE'
            : (firstSubStageOfPhase(config, 'ONBOARDING', 'DRAFT') as AccountsSubStage);

  const isForward =
    !!currentSubStage &&
    !!landingSubStage &&
    landingSubStage !== currentSubStage &&
    isForwardSubStageMove(currentSubStage, landingSubStage);

  const labelFor = (sub: AccountsSubStage): string =>
    findPositionBySystemSubStage(config, sub)?.subStage.label ?? SUB_STAGE_LABEL[sub] ?? sub;

  const passedOverSteps =
    isForward && currentSubStage && landingSubStage
      ? forwardJumpSubStages(config, currentSubStage, landingSubStage, {welcomeCallEnabled}).map(
          (sub) => ({subStage: sub as AccountsSubStage, label: labelFor(sub as AccountsSubStage)}),
        )
      : [];

  const stageResolutions = buildStageResolutions(passedOverSteps, resolutionDrafts);
  // Confirmation requires the loaded account: without `currentSubStage` the
  // forward-jump computation is empty and a multi-stage move would fire with
  // no resolutions, which the server rejects with an opaque BAD_REQUEST.
  const canConfirm =
    !confirming &&
    detail.isSuccess &&
    (!isForward || (!!effectiveDate && stageResolutions !== null));

  // Backward phase drags keep the old "these phases are being skipped" recap.
  const skippedPhases =
    move.kind === 'phase'
      ? PHASE_ORDER.slice(
          Math.min(PHASE_ORDER.indexOf(move.fromPhase), PHASE_ORDER.indexOf(move.toPhase)) + 1,
          Math.max(PHASE_ORDER.indexOf(move.fromPhase), PHASE_ORDER.indexOf(move.toPhase)),
        )
      : [];

  const title = isForward ? 'Confirm forward move' : 'Confirm move';
  const description =
    move.kind === 'phase' ? (
      <>
        Moving from <strong>{PHASE_LABEL[move.fromPhase]}</strong> to{' '}
        <strong>{PHASE_LABEL[move.toPhase]}</strong>.
      </>
    ) : (
      <>
        Moving to{' '}
        <strong>
          {findSubStageByKey(config, move.viewSubStageKey)?.subStage.label ?? 'status'}
        </strong>
        .
      </>
    );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {detail.isLoading ? (
          <p className='py-2 text-sm text-muted-foreground'>Loading account…</p>
        ) : detail.isError ? (
          <p className='py-2 text-sm text-red-600'>
            Couldn&apos;t load the account, so this move can&apos;t be confirmed. Close and try
            again.
          </p>
        ) : isForward && currentSubStage ? (
          <div className='max-h-80 space-y-3 overflow-y-auto pr-1'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <label
                htmlFor='board-move-effective-date'
                className='text-xs font-medium text-foreground'
              >
                When was “{labelFor(currentSubStage)}” completed?
              </label>
              <Input
                id='board-move-effective-date'
                type='date'
                value={effectiveDate}
                max={todayDateInputValue()}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className='h-8 w-44 text-xs'
              />
            </div>
            {passedOverSteps.length > 0 ? (
              <StageResolutionFields
                steps={passedOverSteps}
                drafts={resolutionDrafts}
                onDraftChange={(sub, draft) =>
                  setResolutionDrafts((prev) => ({...prev, [sub]: draft}))
                }
                disabled={confirming}
              />
            ) : null}
          </div>
        ) : skippedPhases.length > 0 ? (
          <ul className='list-disc py-2 pl-6 text-sm'>
            {skippedPhases.map((p) => (
              <li key={p}>{PHASE_LABEL[p]}</li>
            ))}
          </ul>
        ) : null}

        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={confirming}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                stageResolutions: isForward ? (stageResolutions ?? []) : [],
                effectiveDate: isForward && effectiveDate ? dateInputToDate(effectiveDate) : null,
              })
            }
            disabled={!canConfirm}
          >
            {confirming ? 'Moving…' : 'Confirm move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
